/**
 * HeaderMobile Component
 *
 * Renders the mobile header for company details page.
 * Includes company logo, name, headquarters, badges (scoring, pending, register status),
 * industries list, and action buttons.
 *
 * Has two responsive layouts:
 * - Narrow (< 800px): Separate rows for industries and action buttons
 * - Wide (>= 800px): Merged row combining industries and action buttons
 */

import HeaderScoringBadge from './HeaderScoringBadge';
import HeaderIndustriesList from './HeaderIndustriesList';
import { FIELD_SOURCE, dataSourceClass } from '../../../../utils/constants/dataSources';
import WarningBadge from '../../../common/WarningBadge';
import RegisterStatusBadge from '../../../common/RegisterStatusBadge';
import { getLogoUrl, LOGO_PLACEHOLDER } from '../../../../utils/logo';

/**
 * @param {Object} props
 * @param {Object} props.company - Company data with id, name, headquarters, industries, scoring, etc.
 * @param {number} props.windowWidth - Current window width for responsive layout
 * @param {React.ReactNode} props.mobileHeaderActionButtons - Pre-rendered action buttons component
 */
const HeaderMobile = ({ company, windowWidth, mobileHeaderActionButtons }) => {
  const logoUrl = getLogoUrl(company.website_SAFE_LOGO);

  return (
    <div className="company-details-header-mobile">
      <div className="header-row">
        {!!company.isContentPending && (
          <WarningBadge
            badgeId="company-pending-badge-mobile"
            label="Pending completion"
            tooltipContent={
              <>
                This profile is currently being generated and may not yet meet content standards.
              </>
            }
            tooltipPosition="bottom-end"
          />
        )}
        <RegisterStatusBadge company={company} tooltipPosition="bottom-end" />
        {company.showScoring && (
          <HeaderScoringBadge
            companyId={company.id}
            scoreSecondaryColor={company.scoreSecondaryColor}
            scorePrimaryColor={company.scorePrimaryColor}
            scoreIconFilter={company.scoreIconFilter}
            scoreLabel={company.scoreLabel}
            scoreTooltipText={company.scoreTooltipText}
            scoreTooltipClassName={company.scoreTooltipClassName}
            windowWidth={windowWidth}
            variant="mobile"
          />
        )}
        <img className="company-logo" src={logoUrl || LOGO_PLACEHOLDER} alt="company logo" />
        <div className="company-name">{company.name}</div>
        <div className="company-hq">{company.headquarters}</div>
      </div>

      {windowWidth < 800 && (
        <>
          <div className="header-row industries">
            <div className={`industries-title ${dataSourceClass(FIELD_SOURCE.industries)}`}>
              Industries
            </div>
            <HeaderIndustriesList industries={company.industries} emptyClassName="no-industries" />
          </div>
          <div className="header-row">{mobileHeaderActionButtons}</div>
        </>
      )}

      {windowWidth >= 800 && (
        <div className="header-row merged-row">
          <div className={`merged-industries-title ${dataSourceClass(FIELD_SOURCE.industries)}`}>
            Industries
          </div>
          <div className="wrapper">
            <div className="industries-wrapper">
              <HeaderIndustriesList
                industries={company.industries}
                emptyClassName="no-industries"
              />
            </div>
            {mobileHeaderActionButtons}
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderMobile;
